import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertCircle, Check, CheckCircle2, CircleDashed, MinusCircle } from 'lucide-react'
import type {
  ApInvoicePoComparison,
  ApInvoicePoLineComparison,
  ApInvoiceTerms,
} from '@/features/invoices/types'
import { Button } from '@/components/ui/button'
import { cn, formatMoney } from '@/lib/utils'

// More lines than this show only the ones that need a look, with the rest one click away.
const COLLAPSE_AFTER = 8

const VERDICTS = {
  matches: { icon: CheckCircle2, className: 'text-emerald-700' },
  over: { icon: AlertCircle, className: 'text-red-700' },
  under: { icon: MinusCircle, className: 'text-amber-700' },
  no_po: { icon: CircleDashed, className: 'text-muted-foreground' },
  no_total: { icon: CircleDashed, className: 'text-muted-foreground' },
} as const

function verdictText(comparison: ApInvoicePoComparison) {
  const gap = formatMoney(Math.abs(Number(comparison.difference ?? 0)))
  switch (comparison.result) {
    case 'matches':
      return 'Matches the PO'
    case 'over':
      return `${gap} over the PO`
    case 'under':
      return `${gap} under the PO`
    case 'no_po':
      return 'No PO linked'
    default:
      return comparison.invoice_total == null ? 'Invoice total missing' : 'PO total missing'
  }
}

function Figure({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-semibold tabular-nums">{children}</dd>
    </div>
  )
}

function Amount({
  quantity,
  unit,
  amount,
}: {
  quantity?: string | number | null
  unit?: string | null
  amount?: string | null
}) {
  if (quantity == null && amount == null) return <span className="text-muted-foreground">—</span>
  return (
    <span className="flex flex-col items-end">
      <span className="font-medium tabular-nums">{amount == null ? '—' : formatMoney(amount)}</span>
      {quantity != null && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {Number(quantity)}
          {unit != null && ` × ${formatMoney(unit)}`}
        </span>
      )}
    </span>
  )
}

const LINE_STATUS = {
  matches: { label: 'Matches', icon: Check, className: 'text-emerald-700' },
  differs: { label: 'Differs', icon: AlertCircle, className: 'text-red-700' },
  not_on_po: { label: 'Not on PO', icon: AlertCircle, className: 'text-amber-700' },
  not_on_invoice: { label: 'Not on invoice', icon: MinusCircle, className: 'text-muted-foreground' },
} as const

const LINE_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-x-4 sm:grid-cols-[minmax(0,1fr)_8rem_8rem_7.5rem]'

function LineRow({ line, multiplePos }: { line: ApInvoicePoLineComparison; multiplePos: boolean }) {
  const status = LINE_STATUS[line.result]
  const StatusIcon = status.icon
  return (
    <li
      className={cn(
        LINE_GRID,
        'px-4 py-2.5 text-sm',
        line.result === 'differs' && 'bg-red-50/70',
        line.result === 'not_on_po' && 'bg-amber-50/70',
        line.result === 'not_on_invoice' && 'text-muted-foreground',
      )}
    >
      <div className="min-w-0">
        <p className="truncate font-medium">
          {line.model || line.description || 'Line'}
          {multiplePos && line.po_number && (
            <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
              {line.po_number}
            </span>
          )}
        </p>
        {line.model && line.description && (
          <p className="truncate text-xs text-muted-foreground">{line.description}</p>
        )}
        <p className={cn('mt-0.5 flex items-center gap-1 text-xs sm:hidden', status.className)}>
          <StatusIcon className="size-3" aria-hidden="true" /> {status.label}
        </p>
      </div>
      <div className="text-right">
        <Amount quantity={line.po_quantity} unit={line.po_unit_price} amount={line.po_amount} />
      </div>
      <div className="text-right">
        <Amount
          quantity={line.invoice_quantity}
          unit={line.invoice_unit_cost}
          amount={line.invoice_amount}
        />
      </div>
      <p
        className={cn(
          'hidden items-center justify-end gap-1 whitespace-nowrap pt-0.5 text-xs font-medium sm:flex',
          status.className,
        )}
      >
        <StatusIcon className="size-3.5" aria-hidden="true" /> {status.label}
      </p>
    </li>
  )
}

function formatTerms(terms: ApInvoiceTerms | null | undefined) {
  if (!terms?.type) return '—'
  const net = terms.net_days != null ? `Net ${terms.net_days}` : null
  switch (terms.type) {
    case 'discount_net':
      return [
        terms.discount_percent != null && terms.discount_days != null
          ? `${Number(terms.discount_percent)}% ${terms.discount_days}`
          : null,
        net,
      ]
        .filter(Boolean)
        .join(', ')
    case 'net':
      return net ?? 'Net'
    case 'prepaid':
      return 'Prepaid'
    case 'due_on_receipt':
      return 'Due on receipt'
    case 'proximo':
      return 'Proximo'
    default:
      return '—'
  }
}

/** A difference outside the lines (sales tax, payment terms): what we expect beside what the invoice says. */
function DifferenceRow({
  label,
  detail,
  expected,
  billed,
}: {
  label: string
  detail: string
  expected: ReactNode
  billed: ReactNode
}) {
  return (
    <li className={cn(LINE_GRID, 'bg-red-50/70 px-4 py-2.5 text-sm')}>
      <div className="min-w-0">
        <p className="truncate font-medium">{label}</p>
        <p className="truncate text-xs text-muted-foreground">{detail}</p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-red-700 sm:hidden">
          <AlertCircle className="size-3" aria-hidden="true" /> Differs
        </p>
      </div>
      <p className="text-right font-medium tabular-nums">{expected}</p>
      <p className="text-right font-medium tabular-nums">{billed}</p>
      <p className="hidden items-center justify-end gap-1 whitespace-nowrap pt-0.5 text-xs font-medium text-red-700 sm:flex">
        <AlertCircle className="size-3.5" aria-hidden="true" /> Differs
      </p>
    </li>
  )
}

/** Whether the invoice matches its PO, by total and line by line, at the top of the invoice page (USA-1179). */
export function InvoicePoMatch({
  comparison,
  isLoading,
  action,
}: {
  comparison: ApInvoicePoComparison | undefined
  isLoading: boolean
  /** Linking a PO, shown beside the verdict. */
  action?: ReactNode
}) {
  const [showAll, setShowAll] = useState(false)
  if (isLoading || !comparison) {
    return (
      <section
        aria-label="Invoice and PO comparison"
        className="h-[92px] animate-pulse rounded-xl border bg-card"
      />
    )
  }
  const verdict = VERDICTS[comparison.result]
  const VerdictIcon = verdict.icon
  // Without a PO there is nothing to hold the lines against; the invoice PDF shows them.
  const lines = comparison.purchase_orders?.length ? (comparison.lines ?? []) : []
  const needsLook = lines.filter((line) => line.result !== 'matches')
  const collapsed = !showAll && lines.length > COLLAPSE_AFTER && needsLook.length < lines.length
  const shown = collapsed ? needsLook : lines
  const salesTax = Number(comparison.sales_tax ?? 0)
  // What the invoice bills below its lines, less the sales tax that has its own row.
  const otherCharges =
    lines.length && comparison.lines_total != null && comparison.invoice_total != null
      ? Number(comparison.invoice_total) - Number(comparison.lines_total) - salesTax
      : 0
  const others = comparison.other_invoices ?? []
  const multiplePos = (comparison.purchase_orders ?? []).length > 1
  const differences = [
    salesTax > 0.005 && (
      <DifferenceRow
        key="tax"
        label="Sales tax"
        detail="We don't pay sales tax"
        expected={formatMoney(0)}
        billed={formatMoney(salesTax)}
      />
    ),
    comparison.terms_differ && (
      <DifferenceRow
        key="terms"
        label="Payment terms"
        detail="On file for this manufacturer"
        expected={formatTerms(comparison.terms_on_file)}
        billed={formatTerms(comparison.invoice_terms)}
      />
    ),
  ].filter(Boolean)

  return (
    <section aria-label="Invoice and PO comparison" className="overflow-hidden rounded-xl border bg-card">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 gap-y-4 p-4 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
        <p
          role="status"
          className={cn(
            'col-start-1 row-start-1 flex items-center gap-2 text-base font-semibold',
            verdict.className,
          )}
        >
          <VerdictIcon className="size-5 shrink-0" aria-hidden="true" />
          {verdictText(comparison)}
        </p>
        <div className="col-start-2 row-start-1 lg:col-start-3">{action}</div>
        <dl className="col-span-2 row-start-2 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:flex sm:flex-wrap lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:justify-end">
          {(comparison.purchase_orders ?? []).map((po) => (
            <Figure
              key={po.uuid}
              label={
                <Link
                  to="/purchase-orders/$poId"
                  params={{ poId: po.uuid }}
                  className="font-mono text-primary hover:underline"
                >
                  {po.po_number}
                </Link>
              }
            >
              {formatMoney(po.total)}
            </Figure>
          ))}
          <Figure label="Invoice">{formatMoney(comparison.invoice_total)}</Figure>
          {others.length > 0 && (
            <Figure
              label={`With ${others.length} other ${others.length === 1 ? 'invoice' : 'invoices'}`}
            >
              {formatMoney(comparison.billed_total)}
            </Figure>
          )}
          {comparison.difference != null && (
            <Figure label="Difference">
              <span className={verdict.className}>
                {Number(comparison.difference) > 0 ? '+' : ''}
                {formatMoney(comparison.difference)}
              </span>
            </Figure>
          )}
        </dl>
      </div>

      {others.length > 0 && (
        <p className="border-t px-4 py-2 text-xs text-muted-foreground">
          Also billing this PO:{' '}
          {others.map((other, index) => (
            <span key={other.uuid}>
              {index > 0 && ', '}
              <Link
                to="/invoices/$invoiceId"
                params={{ invoiceId: other.uuid }}
                className="text-primary hover:underline"
              >
                {other.invoice_number || 'Invoice'}
              </Link>{' '}
              {formatMoney(other.invoice_total)}
            </span>
          ))}
        </p>
      )}

      {(lines.length > 0 || differences.length > 0) && (
        <div className="border-t">
          <div
            className={cn(
              LINE_GRID,
              'bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground',
            )}
          >
            <span>Item</span>
            <span className="text-right">PO</span>
            <span className="text-right">Invoice</span>
            <span className="hidden sm:block" />
          </div>
          <ul className="divide-y">
            {differences}
            {shown.map((line, index) => (
              <LineRow
                key={`${line.po_number}-${line.display_number}-${line.model}-${index}`}
                line={line}
                multiplePos={multiplePos}
              />
            ))}
            {Math.abs(otherCharges) > 0.01 && (
              <li className={cn(LINE_GRID, 'px-4 py-2.5 text-sm')}>
                <p className="font-medium">Other charges</p>
                <span className="text-right text-muted-foreground">—</span>
                <p className="text-right font-medium tabular-nums">{formatMoney(otherCharges)}</p>
                <span className="hidden sm:block" />
              </li>
            )}
          </ul>
          {lines.length > COLLAPSE_AFTER && needsLook.length < lines.length && (
            <div className="border-t px-2 py-1">
              <Button variant="ghost" size="sm" onClick={() => setShowAll((value) => !value)}>
                {collapsed
                  ? `Show all ${lines.length} lines`
                  : `Show only lines that differ`}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
